import { useMutation } from '@apollo/client';
import {
  IconButton,
  Spacer,
  SvgWatchLaterAdd,
  SvgWatchLaterAdded,
  SvgWatchLaterRemove,
  Tooltip,
} from '@loomhq/lens';
import classnames from 'classnames';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ERROR } from 'constants/error-severities';
import { ROLLOUT_DASHBOARD_WATCH_LATER } from 'constants/featureFlag';

import { useFlagIsActivated } from 'hooks/featureFlag';
import { selectUserIsLogged } from 'selectors/user';

import viewerAddVideoToWatchLaterList from './viewerAddVideoToWatchLaterList.graphql';
import viewerRemoveVideoFromWatchLaterList from './viewerRemoveVideoFromWatchLaterList.graphql';
import { showErrorBar } from '../../../actions/errorBar';

function WatchLaterButton(props) {
  const dispatch = useDispatch();

  const [watchLaterIcon, setWatchLaterIcon] = useState(<SvgWatchLaterAdded />);

  const hasWatchLaterFeatureFlag = useFlagIsActivated({
    flag: ROLLOUT_DASHBOARD_WATCH_LATER,
    activationValues: [true],
  });

  const isLoggedin = useSelector(selectUserIsLogged);

  const [markForWatchLater] = useMutation(viewerAddVideoToWatchLaterList, {
    onCompleted: data => {
      const { result } = data;

      if (result.__typename !== 'ViewerAddVideoToWatchLaterAnswer') {
        dispatch(
          showErrorBar(
            ERROR,
            'An error occured while adding video to Watch Later list'
          )
        );
      }

      setIsOnWatchLater(true);
    },
  });

  const [removeFromWatchLater] = useMutation(
    viewerRemoveVideoFromWatchLaterList,
    {
      onCompleted: data => {
        const { result } = data;

        if (result.__typename !== 'ViewerRemoveVideoFromWatchLaterAnswer') {
          dispatch(
            showErrorBar(
              ERROR,
              'An error occured while removing video from Watch Later list'
            )
          );
        }

        setIsOnWatchLater(false);
      },
    }
  );

  const videoIsMarkedForWatchLater = useSelector(
    state => state.video_player?.video?.viewer_marked_for_watch_later
  );
  // since video model is SSR we should have latest value
  const [isOnWatchLater, setIsOnWatchLater] = useState(
    videoIsMarkedForWatchLater
  );

  const videoId = useSelector(state => state.video_player?.video?.id);

  if (!hasWatchLaterFeatureFlag || !isLoggedin) {
    return null;
  }

  const classNames = classnames('watch-later', props.darkMode && 'theme-dark');

  if (isOnWatchLater) {
    return (
      <>
        <Spacer right="small" />
        <div
          className={classNames}
          onMouseEnter={() => setWatchLaterIcon(<SvgWatchLaterRemove />)}
          onMouseLeave={() => setWatchLaterIcon(<SvgWatchLaterAdded />)}
        >
          <Tooltip
            content="Remove from Watch Later"
            placement="bottomCenter"
            triggerOffset={props.tooltipTriggerOffset || 14}
          >
            <IconButton
              icon={watchLaterIcon}
              onClick={() => removeFromWatchLater({ variables: { videoId } })}
              altText="Remove from Watch Later"
            />
          </Tooltip>
        </div>
      </>
    );
  }

  return (
    <>
      <Spacer right="small" />
      <div className={classNames}>
        <Tooltip
          content="Watch Later (Internal only)"
          placement="bottomCenter"
          triggerOffset={props.tooltipTriggerOffset || 14}
        >
          <IconButton
            icon={<SvgWatchLaterAdd />}
            altText="Watch Later"
            onClick={() => markForWatchLater({ variables: { videoId } })}
          />
        </Tooltip>
      </div>
    </>
  );
}

export default WatchLaterButton;
